import { FormEvent, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { BookingBrandHeader } from "@/components/BookingBrandHeader";
import { CountrySelect } from "@/components/CountrySelect";
import { LicensePlateInput } from "@/components/LicensePlateInput";
import { useLocale } from "@/i18n";
import { api, ApiError } from "@/lib/api";
import { formatCurrency } from "@/lib/money";
import { useCampsiteBranding } from "@/lib/useCampsiteBranding";

type ComfortCheckInData = {
  booking_number: string;
  guest_name: string;
  arrival_date: string;
  departure_date: string;
  pitch_code: string | null;
  license_plate: string | null;
  license_plate_country: string | null;
  stay_total: string;
  amount_due: string;
  currency: string;
  payment_required: boolean;
  price_pending?: boolean;
  stripe_available: boolean;
  completed: boolean;
};

type ComfortCheckInResult = {
  completed: boolean;
  checkout_url: string | null;
  amount_due: string;
  currency: string;
};

function formatDate(iso: string, locale: string): string {
  const [year, month, day] = iso.split("-");
  if (!year || !month || !day) return iso;
  if (locale === "en") return `${day}/${month}/${year}`;
  return `${day}.${month}.${year}`;
}

export function ComfortCheckInPage() {
  const { token = "" } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const { locale, localePath, messages } = useLocale();
  const copy = messages.comfortCheckIn;
  const { siteName, logoUrl } = useCampsiteBranding();
  const paidReturn = searchParams.get("paid") === "1";
  const cancelledReturn = searchParams.get("cancelled") === "1";

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [confirmingPayment, setConfirmingPayment] = useState(paidReturn);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(cancelledReturn ? copy.paymentCancelled : null);
  const [data, setData] = useState<ComfortCheckInData | null>(null);
  const [plate, setPlate] = useState("");
  const [country, setCountry] = useState("DE");

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    let attempts = 0;

    async function load() {
      try {
        const res = await api.get<ComfortCheckInData>(`/api/v1/public/comfort-check-in/${token}`);
        if (cancelled) return;
        setData(res);
        setPlate((current) => current || res.license_plate || "");
        setCountry(res.license_plate_country || "DE");
        setError(null);
        if (res.completed || !paidReturn) {
          setConfirmingPayment(false);
          setLoading(false);
          return;
        }
        attempts += 1;
        if (attempts >= 15) {
          setConfirmingPayment(false);
          setLoading(false);
          setError(copy.paymentPending);
          return;
        }
        window.setTimeout(() => {
          if (!cancelled) void load();
        }, 2000);
      } catch (err) {
        if (cancelled) return;
        setConfirmingPayment(false);
        setLoading(false);
        setError(err instanceof ApiError ? err.message : copy.invalidLink);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [token, paidReturn, copy.invalidLink, copy.paymentPending]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!data || data.completed) return;
    if (plate.trim().length < 2) {
      setError(copy.plateRequired);
      return;
    }
    setError(null);
    setNotice(null);
    setSubmitting(true);
    const success = new URL(window.location.href);
    success.searchParams.set("paid", "1");
    success.searchParams.delete("cancelled");
    const cancel = new URL(window.location.href);
    cancel.searchParams.set("cancelled", "1");
    cancel.searchParams.delete("paid");
    try {
      const result = await api.post<ComfortCheckInResult>(
        `/api/v1/public/comfort-check-in/${token}`,
        {
          license_plate: plate.trim(),
          license_plate_country: country,
          success_url: success.toString(),
          cancel_url: cancel.toString(),
        },
      );
      if (result.checkout_url) {
        window.location.assign(result.checkout_url);
        return;
      }
      setData((current) =>
        current
          ? {
              ...current,
              completed: true,
              payment_required: false,
              license_plate: plate.trim(),
              license_plate_country: country,
            }
          : current,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : copy.saveError);
      setSubmitting(false);
    }
  }

  const showForm = data && !data.completed && !confirmingPayment;
  const paymentBlocked = Boolean(showForm && data?.payment_required && !data.stripe_available);

  return (
    <div className="mx-auto min-h-screen max-w-lg px-4 py-10">
      <BookingBrandHeader
        siteName={siteName}
        logoUrl={logoUrl}
        title={copy.pageTitle}
        subtitle={copy.pageSubtitle}
        className="mb-8"
      />

      <div className="bk-card space-y-4">
        {loading || confirmingPayment ? (
          <p className="text-sm text-slate-600">
            {confirmingPayment ? copy.confirmingPayment : copy.loading}
          </p>
        ) : error && !data ? (
          <div className="space-y-3">
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
              {error}
            </div>
            <p className="text-center text-sm text-slate-500">
              <Link to={localePath("/")} className="text-brand-700 underline">
                {copy.toBooking}
              </Link>
            </p>
          </div>
        ) : data?.completed ? (
          <div className="space-y-3">
            <div className="rounded-lg border border-brand-200 bg-brand-50 px-4 py-3 text-sm text-brand-900">
              {copy.completed}
            </div>
            <p className="text-sm text-slate-600">
              {copy.bookingNumber}: <strong>{data.booking_number}</strong>
            </p>
            {data.license_plate ? (
              <p className="text-sm text-slate-600">
                {copy.licensePlate}: <strong>{data.license_plate}</strong>
              </p>
            ) : null}
          </div>
        ) : showForm && data ? (
          <form className="space-y-5" onSubmit={(event) => void onSubmit(event)}>
            <div className="space-y-1 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              <p>
                <span className="text-slate-500">{copy.booking}:</span> {data.booking_number}
              </p>
              <p>
                <span className="text-slate-500">{copy.guest}:</span> {data.guest_name}
              </p>
              <p>
                <span className="text-slate-500">{copy.period}:</span>{" "}
                {formatDate(data.arrival_date, locale)} – {formatDate(data.departure_date, locale)}
              </p>
              {data.pitch_code ? (
                <p>
                  <span className="text-slate-500">{copy.pitch}:</span> {data.pitch_code}
                </p>
              ) : null}
              <p>
                <span className="text-slate-500">{copy.stayTotal}:</span>{" "}
                {data.price_pending ? copy.priceFollows : formatCurrency(data.stay_total, data.currency, locale)}
              </p>
              {data.price_pending ? null : (
              <p className="font-medium text-slate-900">
                <span className="font-normal text-slate-500">{copy.amountDue}:</span>{" "}
                {formatCurrency(data.amount_due, data.currency, locale)}
              </p>
              )}
            </div>

            {data.price_pending ? (
              <p className="text-sm text-amber-900">{copy.pricePending}</p>
            ) : !data.payment_required ? (
              <p className="text-sm text-emerald-800">{copy.alreadyPaid}</p>
            ) : null}
            {notice ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="status">
                {notice}
              </div>
            ) : null}
            {paymentBlocked ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="status">
                {copy.stripeUnavailable}
              </div>
            ) : null}

            <div className="grid gap-3 sm:grid-cols-[1fr_8rem]">
              <label className="block text-sm font-medium text-slate-700">
                {copy.licensePlate} *
                <LicensePlateInput value={plate} onChange={setPlate} required />
              </label>
              <label className="block text-sm font-medium text-slate-700">
                {copy.licensePlateCountry}
                <CountrySelect value={country} onChange={setCountry} />
              </label>
            </div>

            {error ? (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
                {error}
              </div>
            ) : null}

            <button type="submit" className="bk-btn-primary w-full" disabled={submitting || paymentBlocked}>
              {submitting ? copy.redirecting : data.payment_required ? copy.pay : copy.completeFree}
            </button>
          </form>
        ) : null}
      </div>
    </div>
  );
}
